// Single source of truth for admin navigation: the top bar (TopNavBarAdmin and
// the admin branch of ClientSideMenuButtons), the account dropdown (MenuAdmin),
// breadcrumbs and the dashboard tools grid all read from here.

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();export type AdminNavIcon =
  | "dashboard"
  | "users"
  | "groups"
  | "orgs"
  | "articles"
  | "newsletter"
  | "campaigns"
  | "email"
  | "home"
  | "aiUsage"

export type AdminNavKey =
  | "dashboard"
  | "users"
  | "groups"
  | "orgs"
  | "articles"
  | "newsletter"
  | "email"
  | "home"
  | "aiUsage"

export type AdminNavItem = {
  key: AdminNavKey
  label: string
  href: string
  icon: AdminNavIcon
  // Pathname prefixes (besides href) that mark this item as the current page.
  matchPrefixes: readonly string[]
}

export const ADMIN_DASHBOARD_NAV: AdminNavItem = {
  key: "dashboard",
  label: "Admin dashboard",
  href: "/admin",
  icon: "dashboard",
  matchPrefixes: [],
}

// Shown in the top bar (and in the dropdown's "Manage" group on small screens).
export const ADMIN_PRIMARY_NAV: readonly AdminNavItem[] = [
  {
    key: "users",
    label: "Users",
    href: "/admin/users",
    icon: "users",
    matchPrefixes: ["/admin/users", "/admin/user/"],
  },
  {
    key: "groups",
    label: "Groups",
    href: "/admin/groups",
    icon: "groups",
    matchPrefixes: [
      "/admin/groups",
      "/admin/group/",
      "/admin/cohort/",
      "/admin/session/",
    ],
  },
  {
    key: "orgs",
    label: "Orgs",
    href: "/admin/organizations",
    icon: "orgs",
    matchPrefixes: ["/admin/organizations", "/admin/organization/"],
  },
  {
    key: "articles",
    label: "Articles",
    href: "/admin/articles",
    icon: "articles",
    // Admin article review/editing happens in the studio routes.
    matchPrefixes: ["/admin/articles", "/dashboard/studio/"],
  },
]

// Only in the account dropdown (and as dashboard tiles).
export const ADMIN_TOOLS_NAV: readonly AdminNavItem[] = [
  {
    key: "newsletter",
    label: "Newsletter",
    href: "/admin/newsletter",
    icon: "newsletter",
    matchPrefixes: ["/admin/newsletter"],
  },
  {
    key: "email",
    label: "Email",
    href: "/admin/email",
    icon: "email",
    matchPrefixes: ["/admin/email"],
  },
  {
    key: "home",
    label: "Home page",
    href: "/admin/home",
    icon: "home",
    matchPrefixes: ["/admin/home"],
  },
  {
    key: "aiUsage",
    label: "AI usage",
    href: "/admin/ai-usage",
    icon: "aiUsage",
    matchPrefixes: ["/admin/ai-usage"],
  },
]

export const ADMIN_NAV_ITEMS: readonly AdminNavItem[] = [
  ADMIN_DASHBOARD_NAV,
  ...ADMIN_PRIMARY_NAV,
  ...ADMIN_TOOLS_NAV,
]

export function adminNavItem(key: AdminNavKey): AdminNavItem {
  return ADMIN_NAV_ITEMS.find((item) => item.key === key)!
}

const matchesPrefix = (pathname: string, prefix: string) =>
  prefix.endsWith("/")
    ? pathname.startsWith(prefix)
    : pathname === prefix || pathname.startsWith(`${prefix}/`)

// The nav item for the page being viewed, or null (e.g. public pages).
export function activeAdminNavKey(
  pathname: string | null | undefined,
): AdminNavKey | null {
  if (!pathname) return null
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, "") || "/"
  if (path === ADMIN_DASHBOARD_NAV.href) return "dashboard"
  const item = [...ADMIN_PRIMARY_NAV, ...ADMIN_TOOLS_NAV].find((candidate) =>
    candidate.matchPrefixes.some((prefix) => matchesPrefix(path, prefix)),
  )
  return item?.key ?? null
}

// aria-current for the active nav item: "page" on the item's own page, "true"
// on another page inside its section (e.g. a group detail page for Groups).
export function adminNavAriaCurrent(
  pathname: string | null | undefined,
  key: AdminNavKey | null,
): "page" | "true" | undefined {
  if (!key || activeAdminNavKey(pathname) !== key) return undefined
  const path = (pathname ?? "").split(/[?#]/)[0].replace(/\/+$/, "") || "/"
  return path === adminNavItem(key).href ? "page" : "true"
}

// ---------------------------------------------------------------- breadcrumbs

export type AdminCrumb = { label: string; href: string }

export type AdminBreadcrumbTrail = {
  // Linked ancestors, root first. The current page is not part of the trail.
  trail: AdminCrumb[]
  current: string
}

// Detail pages know entity names only after their data loads, so they pass
// them in. `trail` replaces everything between the root crumb and the current
// page; `current` replaces the fallback label ("Group 45").
export type AdminBreadcrumbOverrides = {
  trail?: AdminCrumb[]
  current?: string | null
}

export const ADMIN_ROOT_CRUMB: AdminCrumb = {
  label: "Dashboard",
  href: ADMIN_DASHBOARD_NAV.href,
}

export function adminNavCrumb(key: AdminNavKey): AdminCrumb {
  const { label, href } = adminNavItem(key)
  return { label, href }
}

type AdminBreadcrumbRoute = {
  // Absolute path; a ":param" segment matches any single segment.
  pattern: string
  // null: the page shows no breadcrumb strip.
  crumbs: ((param: string) => { parents: AdminCrumb[]; current: string }) | null
}

const listPage = (key: AdminNavKey) => () => ({
  parents: [],
  current: adminNavItem(key).label,
})

const CAMPAIGNS_CRUMB: AdminCrumb = {
  label: "Campaigns",
  href: "/admin/newsletter/campaigns",
}

// Every page under src/app/admin (tests/unit/lib/admin/adminNav.spec.ts fails
// when a page is added without an entry here). Detail routes are singular and
// their list parents plural.
const ADMIN_BREADCRUMB_ROUTES: readonly AdminBreadcrumbRoute[] = [
  { pattern: "/admin", crumbs: null },
  { pattern: "/admin/users", crumbs: listPage("users") },
  {
    pattern: "/admin/user/:id",
    crumbs: (id) => ({ parents: [adminNavCrumb("users")], current: `User ${id}` }),
  },
  { pattern: "/admin/groups", crumbs: listPage("groups") },
  {
    pattern: "/admin/groups/search-synonyms",
    crumbs: () => ({
      parents: [adminNavCrumb("groups")],
      current: "Search aliases",
    }),
  },
  {
    pattern: "/admin/group/:id",
    crumbs: (id) => ({ parents: [adminNavCrumb("groups")], current: `Group ${id}` }),
  },
  {
    pattern: "/admin/cohort/:id",
    crumbs: (id) => ({
      parents: [adminNavCrumb("groups")],
      current: `Cohort ${id}`,
    }),
  },
  {
    pattern: "/admin/session/:id",
    crumbs: (id) => ({
      parents: [adminNavCrumb("groups")],
      current: `Session ${id}`,
    }),
  },
  { pattern: "/admin/organizations", crumbs: listPage("orgs") },
  {
    // The slug is a uuid, so the fallback carries no id.
    pattern: "/admin/organization/:id",
    crumbs: () => ({ parents: [adminNavCrumb("orgs")], current: "Organization" }),
  },
  { pattern: "/admin/articles", crumbs: listPage("articles") },
  { pattern: "/admin/newsletter", crumbs: listPage("newsletter") },
  {
    pattern: "/admin/newsletter/campaigns",
    crumbs: () => ({ parents: [adminNavCrumb("newsletter")], current: "Campaigns" }),
  },
  {
    pattern: "/admin/newsletter/campaigns/:id",
    crumbs: (id) => ({
      parents: [adminNavCrumb("newsletter"), CAMPAIGNS_CRUMB],
      current: `Campaign ${id}`,
    }),
  },
  { pattern: "/admin/email", crumbs: listPage("email") },
  { pattern: "/admin/home", crumbs: listPage("home") },
  // The preview renders the public home page; a strip would make it unfaithful.
  { pattern: "/admin/home/preview", crumbs: null },
  { pattern: "/admin/ai-usage", crumbs: listPage("aiUsage") },
  // Deprecated and reachable only by URL, so it has no nav item.
  {
    pattern: "/admin/provider-onboarding",
    crumbs: () => ({ parents: [], current: "Provider onboarding" }),
  },
]

export const ADMIN_BREADCRUMB_ROUTE_PATTERNS: readonly string[] =
  ADMIN_BREADCRUMB_ROUTES.map((route) => route.pattern)

const pathSegments = (path: string) => path.split("/").filter(Boolean)

function matchRoute(
  pathname: string,
): { route: AdminBreadcrumbRoute; param: string } | null {
  const segments = pathSegments(pathname.split(/[?#]/)[0])
  for (const route of ADMIN_BREADCRUMB_ROUTES) {
    const patternSegments = pathSegments(route.pattern)
    if (patternSegments.length !== segments.length) continue
    let param = ""
    const matches = patternSegments.every((part, index) => {
      if (!part.startsWith(":")) return part === segments[index]
      param = segments[index]
      return true
    })
    if (matches) return { route, param }
  }
  return null
}

// The breadcrumb for an admin page, or null when the page shows none (the
// dashboard itself, the home preview, anything outside the route table).
export function buildAdminBreadcrumbs(
  pathname: string | null | undefined,
  overrides?: AdminBreadcrumbOverrides,
): AdminBreadcrumbTrail | null {
  if (!pathname) return null
  const match = matchRoute(pathname)
  if (!match?.route.crumbs) return null
  const { parents, current } = match.route.crumbs(match.param)
  const currentOverride = overrides?.current?.trim()
  return {
    trail: [ADMIN_ROOT_CRUMB, ...(overrides?.trail ?? parents)],
    current: currentOverride || current,
  }
}
